import { type ReactElement } from "react";
import { Info } from "lucide-react";
import { Button } from "../../atoms/Button/index.js";
import { cn } from "../../lib/utils.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../molecules/Popover/index.js";

export type SipocColumnId =
  | "suppliers"
  | "inputs"
  | "process"
  | "outputs"
  | "customers";

/** The five column titles, left to right along the board. */
export const SIPOC_COLUMN_TITLES: Record<SipocColumnId, string> = {
  suppliers: "Suppliers",
  inputs: "Inputs",
  process: "Process",
  outputs: "Outputs",
  customers: "Customers",
};

/**
 * What goes in each column. ONE source for two readers: the board's column
 * headers carry each line as screen-reader-only text, and the guide popover
 * lists them. Change the words here, nowhere else.
 */
export const SIPOC_COLUMN_HINTS: Record<SipocColumnId, string> = {
  suppliers: "Suppliers feed each process step.",
  inputs: "Inputs feed each process step.",
  process: "Start with the Process spine.",
  outputs: "Outputs flow from each process step.",
  customers: "Customers receive each step's outputs.",
};

const GUIDE_COLUMN_ORDER: readonly SipocColumnId[] = [
  "suppliers",
  "inputs",
  "process",
  "outputs",
  "customers",
];

export interface SipocGuideButtonProps {
  /** Layout only: placement of the button's wrapper inside a host row. */
  readonly className?: string;
}

/**
 * The (i) beside a SIPOC title. An icon-only button that opens a popover with
 * the SIPOC guide line, then one "Column — hint" line per column. The board
 * itself keeps only its five column headers; the explaining lives here, in the
 * host's title row, so it is one click away without taking board height.
 *
 * Data-blind: the copy is fixed. The popover is a non-modal dialog, so Escape
 * or an outside press closes it and focus returns to this button.
 */
export function SipocGuideButton({
  className,
}: SipocGuideButtonProps): ReactElement {
  return (
    // `inline-flex` wrapper: the button is a flex item, so the wrapper is
    // exactly the button's 24px and a line box's strut cannot make it taller.
    <Popover
      contentRole="dialog"
      align="start"
      wrapperClassName={cn("inline-flex", className)}
    >
      <PopoverTrigger>
        <Button variant="ghost" size="xs" iconOnly aria-label="About SIPOC">
          <Info className="h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        padding="lg"
        className="w-96 max-w-[calc(100vw-2rem)]"
        aria-label="About SIPOC"
      >
        <p className="text-sm leading-5 text-pb-text-secondary">
          SIPOC = Suppliers · Inputs · Process · Outputs · Customers. Scope
          before you map. Start with the{" "}
          <span className="font-semibold text-pb-text">Process</span> spine,
          then work outward —{" "}
          <span className="font-semibold text-pb-text">Suppliers → Inputs</span>{" "}
          feed each step;{" "}
          <span className="font-semibold text-pb-text">
            Outputs → Customers
          </span>{" "}
          flow from it. Drag the left handle to reorder steps or entries.
        </p>
        <ul className="mt-3 space-y-1.5 border-t border-pb-border pt-3 text-sm leading-5 text-pb-text-secondary">
          {GUIDE_COLUMN_ORDER.map((column) => (
            <li key={column}>
              <span className="font-semibold text-pb-text">
                {SIPOC_COLUMN_TITLES[column]}
              </span>{" "}
              — {SIPOC_COLUMN_HINTS[column]}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
